/**
 * useAutosave Hook
 * 
 * Manages autosave logic with:
 * - Debounce: waits X seconds after last change before saving
 * - Max interval: forces save if document has been dirty too long
 * - Coordination with manual saves
 */

import { useRef, useCallback, useEffect, useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

/** Autosave configuration (React-owned, no longer from Angular) */
export interface AutosaveConfig {
  enabled: boolean;
  debounceMs: number;
  maxIntervalMs: number;
}

export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface AutosaveState {
  status: AutosaveStatus;
  lastSavedAt: number | null;
  error: string | null;
}

export interface UseAutosaveOptions {
  config: AutosaveConfig | undefined;
  isDirty: boolean;
  canSave: boolean;
  onSave: () => void | Promise<void>;
}

export interface UseAutosaveReturn {
  state: AutosaveState;
  
  /** Call when content changes */
  onContentChange: () => void;
  
  /** Call when manual save starts (pauses autosave) */
  onManualSaveStart: () => void;
  
  /** Call when any save completes successfully */
  onSaveComplete: () => void;
  
  /** Call when autosave fails */
  onSaveFailed: (error: string) => void;
  
  /** Call when save as completes (resets state) */
  onSaveAsComplete: () => void;
  
  /** Force reset (e.g., document reload) */
  reset: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

export function useAutosave({
  config,
  isDirty,
  canSave,
  onSave,
}: UseAutosaveOptions): UseAutosaveReturn {
  // State
  const [state, setState] = useState<AutosaveState>({
    status: 'idle',
    lastSavedAt: null,
    error: null,
  });

  // Refs for timers and tracking
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxIntervalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstDirtyAtRef = useRef<number | null>(null);
  const isManualSavingRef = useRef(false);
  const isSavingRef = useRef(false);
  const isDirtyRef = useRef(isDirty);
  const canSaveRef = useRef(canSave);
  const configRef = useRef(config);

  // Keep latest values in refs so timer callbacks never read stale render values.
  useEffect(() => {
    isDirtyRef.current = isDirty;
    canSaveRef.current = canSave;
    configRef.current = config;
  }, [isDirty, canSave, config]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (maxIntervalTimerRef.current) {
      clearTimeout(maxIntervalTimerRef.current);
      maxIntervalTimerRef.current = null;
    }
    if (statusResetTimerRef.current) {
      clearTimeout(statusResetTimerRef.current);
      statusResetTimerRef.current = null;
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Trigger autosave
  const triggerAutosave = useCallback(() => {
    const currentConfig = configRef.current;

    // Guard conditions
    if (!currentConfig?.enabled) return;
    if (!canSaveRef.current) return;
    if (isManualSavingRef.current) return;
    if (isSavingRef.current) return;
    if (!isDirtyRef.current) return;

    isSavingRef.current = true;
    setState(prev => ({ ...prev, status: 'saving', error: null }));
    
    onSave();
  }, [onSave]);

  // Start/restart debounce timer
  const startDebounceTimer = useCallback(() => {
    const currentConfig = configRef.current;
    if (!currentConfig?.enabled || !canSaveRef.current) return;

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      triggerAutosave();
    }, currentConfig.debounceMs);
  }, [triggerAutosave]);

  // Start max interval timer (only once when first dirty)
  const startMaxIntervalTimer = useCallback(() => {
    const currentConfig = configRef.current;
    if (!currentConfig?.enabled || !canSaveRef.current) return;
    if (maxIntervalTimerRef.current) return; // Already running

    maxIntervalTimerRef.current = setTimeout(() => {
      console.log('[useAutosave] Max interval reached, forcing save');
      triggerAutosave();
    }, currentConfig.maxIntervalMs);

    console.log('[useAutosave] Max interval timer started:', currentConfig.maxIntervalMs, 'ms');
  }, [triggerAutosave]);

  // ===========================================================================
  // PUBLIC CALLBACKS
  // ===========================================================================

  const onContentChange = useCallback(() => {
    const currentConfig = configRef.current;
    const currentCanSave = canSaveRef.current;

    if (!currentConfig?.enabled) return;
    if (!currentCanSave) return;

    // Track when document first became dirty
    if (firstDirtyAtRef.current === null) {
      firstDirtyAtRef.current = Date.now();
      startMaxIntervalTimer();
    }

    // Update status to pending
    setState(prev => {
      if (prev.status === 'idle' || prev.status === 'saved') {
        return { ...prev, status: 'pending' };
      }
      return prev;
    });

    // Restart debounce timer
    startDebounceTimer();
  }, [config?.enabled, startDebounceTimer, startMaxIntervalTimer]);

  const onManualSaveStart = useCallback(() => {
    console.log('[useAutosave] Manual save started, pausing autosave');
    isManualSavingRef.current = true;
    clearTimers();
  }, [clearTimers]);

  const onSaveComplete = useCallback(() => {
    console.log('[useAutosave] Save completed');
    
    isSavingRef.current = false;
    isManualSavingRef.current = false;
    firstDirtyAtRef.current = null;
    
    clearTimers();
    
    setState({
      status: 'saved',
      lastSavedAt: Date.now(),
      error: null,
    });

    // Reset to idle after showing "Saved" briefly
    if (statusResetTimerRef.current) {
      clearTimeout(statusResetTimerRef.current);
    }
    statusResetTimerRef.current = setTimeout(() => {
      setState(prev => {
        if (prev.status === 'saved') {
          return { ...prev, status: 'idle' };
        }
        return prev;
      });
      statusResetTimerRef.current = null;
    }, 3000);
  }, [clearTimers]);

  const onSaveFailed = useCallback((error: string) => {
    console.log('[useAutosave] Save failed:', error);
    
    isSavingRef.current = false;
    isManualSavingRef.current = false;
    
    setState(prev => ({
      ...prev,
      status: 'error',
      error,
    }));

    // Retry after delay if still dirty
    if (config?.enabled && canSave) {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      retryTimerRef.current = setTimeout(() => {
        if (isDirty) {
          startDebounceTimer();
        }
        retryTimerRef.current = null;
      }, 10000); // Retry after 10 seconds
    }
  }, [config?.enabled, canSave, isDirty, startDebounceTimer]);

  const onSaveAsComplete = useCallback(() => {
    console.log('[useAutosave] Save As completed, resetting state');
    
    isSavingRef.current = false;
    isManualSavingRef.current = false;
    firstDirtyAtRef.current = null;
    
    clearTimers();
    
    setState({
      status: 'idle',
      lastSavedAt: null,
      error: null,
    });
  }, [clearTimers]);

  const reset = useCallback(() => {
    console.log('[useAutosave] Resetting');
    
    isSavingRef.current = false;
    isManualSavingRef.current = false;
    firstDirtyAtRef.current = null;
    
    clearTimers();
    
    setState({
      status: 'idle',
      lastSavedAt: null,
      error: null,
    });
  }, [clearTimers]);

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  // Reset when config changes (e.g., permissions revoked)
  useEffect(() => {
    if (!config?.enabled || !canSave) {
      clearTimers();
      if (state.status === 'pending') {
        setState(prev => ({ ...prev, status: 'idle' }));
      }
    }
  }, [config?.enabled, canSave, clearTimers, state.status]);

  return {
    state,
    onContentChange,
    onManualSaveStart,
    onSaveComplete,
    onSaveFailed,
    onSaveAsComplete,
    reset,
  };
}

export default useAutosave;